/**
 * Join links, both ends of them (3056, docs/JOIN-LINKS.md).
 *
 * A NEW SLICE RATHER THAN MORE OF `invites.ts`, whose own header opens "TWO
 * AUDIENCES IN ONE SLICE". This would have made four, and they do not overlap:
 * an invitation names one address and is spent once, a join link names nobody
 * and is held by everybody the leader gave it to.
 *
 * The two audiences here, which never overlap in one session either:
 *
 *   - THE LEADER mints one link, copies it, rotates or turns it off, and works
 *     a REQUESTS list. Everything needs a session and a party they own.
 *   - THE ASKER opens the link, proves an address if they have no account,
 *     asks, and then WAITS. `peekLink`, `sendAskCode` and `submitAsk` run
 *     before sign-in; `askStatus` and `withdrawAsk` run after.
 *
 * WHAT MARCUS ASKED FOR, and the shape of it: "anyone joining via this link
 * must first be approved by the party leader before being officially added to
 * the party", then "enforce email verification through this path", then "the
 * accounts that exist now are all considered to have their emails verified as
 * if they did it the new way. plain and simple." So: a request is NEVER a
 * membership, a stranger proves an address before an account is minted for
 * them, and somebody who already has an account just asks.
 *
 * THE ASK SCREEN IS TWO STEPS ON ONE SCREEN. Address first, so the code is in
 * flight while they fill in the rest. `codeSent` is the hinge.
 */

import type { StateCreator } from 'zustand';

import type { JoinLinkPeek, JoinRequest, JoinStatus } from '../../repo/Repository.ts';
import { RepoError } from '../../repo/Repository.ts';
import { clearedCrewState } from './crews.ts';
import type { QdStore } from '../types.ts';

export interface JoinLinksSlice {
  /* -- the leader's side ------------------------------------------------- */
  /** Is there a live link for the active party. */
  jlLive: boolean;
  /** When it was minted, ISO, or ''. */
  jlCreatedAt: string;
  /**
   * The link, ready to copy.
   *
   * RE-FETCHABLE FROM 3057, which reversed 3056's hash-only storage: a join
   * link only buys the right to ask, and one meant to be pasted into a group
   * chat has to be readable more than once. Empty means either no live link or
   * one minted before 3057, which genuinely cannot be shown.
   */
  jlUrl: string;
  /** There is a live link but nothing stored to display — minted pre-3057.
   *  Its own flag so the surface can say why rather than showing a blank. */
  jlUnreadable: boolean;
  /** A write is open: minting, revoking, or deciding a request. */
  jlBusy: string;
  /** Open requests for the active party, newest first. */
  jlRequests: JoinRequest[];

  /* -- the asker's side -------------------------------------------------- */
  /** The token from `#key=…`, held while the ASK screen works. */
  askToken: string;
  askPeek: JoinLinkPeek | null;
  askEmail: string;
  /** Step two is open: a code is in flight and the rest of the form is up. */
  askCodeSent: boolean;
  askMasked: string;
  askCode: string;
  askName: string;
  askPw: string;
  /**
   * Typed again, for the reason `invites.ts` records at `joinPw2` and which
   * applies verbatim here: this is the only credential a brand-new account
   * will ever have, chosen on a screen reached from a link. A typo burns the
   * whole attempt — and here it also burns a mailed code.
   */
  askPw2: string;
  askErr: string;
  askBusy: boolean;
  /**
   * The address already has an account, so the only way forward is to log in.
   *
   * ITS OWN FLAG AND NOT JUST AN ERROR STRING, because the screen changes
   * shape rather than gaining a red line: there is nothing to retype, and the
   * useful control is a door out. `askToken` is kept so signing in returns
   * them here rather than making them find the link again.
   */
  askKnown: boolean;
  /** Waiting on a leader, from `status`. */
  askWaiting: JoinStatus | null;

  setAskForm: (patch: Partial<
    Pick<JoinLinksSlice, 'askEmail' | 'askCode' | 'askName' | 'askPw' | 'askPw2'>
  >) => void;

  loadJoinLink: () => Promise<void>;
  newJoinLink: () => Promise<void>;
  offJoinLink: () => Promise<void>;
  loadJoinRequests: () => Promise<void>;
  decideRequest: (id: string, decision: 'approve' | 'deny') => Promise<void>;

  openAsk: (token: string) => Promise<void>;
  sendAskCode: () => Promise<void>;
  submitAsk: () => Promise<void>;
  loadAskStatus: () => Promise<boolean>;
  withdrawAsk: () => Promise<void>;
}

const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

/**
 * Where the ask token survives a reload.
 *
 * IT HAS TO SURVIVE ONE, and that is not a nicety. `boot` scrubs the `#key=`
 * fragment the instant it reads it — correctly, because a link token is bearer
 * authority in the address bar — so the token then exists ONLY in memory. Any
 * refresh, any "open in a new tab", any trip through the login screen that
 * reloads the document, and the flow is unrecoverable: unlike an invitation,
 * there is no email to go back to. The link came from a friend's chat.
 *
 * `sessionStorage`, NOT `localStorage`: it is a credential, so it should die
 * with the tab rather than sit on the disk of a shared machine.
 */
const ASK_KEY = 'qd:ask';

function rememberAsk(token: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(ASK_KEY, token);
  } catch {
    // Safari's private mode throws on write. The in-memory path still works;
    // only surviving a reload is lost, which is what this is best-effort about.
  }
}

/** The token a previous page load was part-way through, or ''. */
export function rememberedAsk(): string {
  try {
    return typeof sessionStorage === 'undefined' ? '' : (sessionStorage.getItem(ASK_KEY) ?? '');
  } catch {
    return '';
  }
}

function forgetAsk(): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(ASK_KEY);
  } catch {
    /* see rememberAsk */
  }
}

export const createJoinLinksSlice: StateCreator<QdStore, [], [], JoinLinksSlice> = (
  set,
  get,
) => ({
  jlLive: false,
  jlCreatedAt: '',
  jlUrl: '',
  jlUnreadable: false,
  jlBusy: '',
  jlRequests: [],

  askToken: '',
  askPeek: null,
  askEmail: '',
  askCodeSent: false,
  askMasked: '',
  askCode: '',
  askName: '',
  askPw: '',
  askPw2: '',
  askErr: '',
  askBusy: false,
  askKnown: false,
  askWaiting: null,

  setAskForm: (patch) => set(patch),

  loadJoinLink: async () => {
    try {
      const s = await get().repo.partyJoinLink();
      set({
        jlLive: s.live,
        jlCreatedAt: s.createdAt ?? '',
        jlUrl: s.joinUrl ?? '',
        jlUnreadable: s.live && !s.joinUrl,
      });
    } catch {
      // Same reasoning as `loadPending`: a failed read is not worth a toast.
      // The party still works, and blanking the block on one bad fetch would
      // be worse than leaving the previous answer up.
    }
  },

  newJoinLink: async () => {
    const s = get();
    if (s.jlBusy) return;
    set({ jlBusy: 'link' });
    try {
      const res = await s.repo.newPartyJoinLink();
      const had = s.jlLive;
      set({ jlLive: true, jlUrl: res.joinUrl, jlUnreadable: false });
      await get().loadJoinLink();
      // Says the old one is dead because it IS, and because we cannot show it
      // again to prove otherwise — the same truth `sendInvite`'s resend toast
      // has to tell.
      s.toast(had ? 'NEW LINK — THE OLD ONE NO LONGER WORKS' : 'JOIN LINK READY — COPY IT');
    } catch {
      s.toast("✕ COULDN'T MAKE A JOIN LINK — TRY AGAIN", true);
    } finally {
      set({ jlBusy: '' });
    }
  },

  offJoinLink: async () => {
    const s = get();
    if (s.jlBusy) return;
    set({ jlBusy: 'link' });
    try {
      await s.repo.offPartyJoinLink();
      set({ jlLive: false, jlUrl: '', jlCreatedAt: '', jlUnreadable: false });
      s.toast('JOIN LINK TURNED OFF');
    } catch {
      s.toast("✕ COULDN'T TURN THAT LINK OFF — TRY AGAIN", true);
    } finally {
      set({ jlBusy: '' });
    }
  },

  loadJoinRequests: async () => {
    try {
      set({ jlRequests: await get().repo.listJoinRequests() });
    } catch {
      /* leave the previous answer up — see `loadJoinLink` */
    }
  },

  decideRequest: async (id, decision) => {
    const s = get();
    if (s.jlBusy) return;
    const who = s.jlRequests.find((r) => r.id === id)?.displayName ?? '';
    set({ jlBusy: id });
    try {
      await s.repo.decideJoinRequest(id, decision);
      await get().loadJoinRequests();
      if (decision === 'approve') {
        /* THE ROSTER CHANGED, so the roster has to be refetched. Approving is
           the one action on this screen that adds a member, and MANAGE PARTY
           is showing the old list until something reloads it. */
        await get().hydrateAfterAuth();
        s.toast((who ? who.toUpperCase() + ' IS IN' : 'THEY ARE IN'));
      } else {
        s.toast(who ? 'TURNED DOWN ' + who.toUpperCase() : 'REQUEST TURNED DOWN');
      }
    } catch (e) {
      const code = e instanceof RepoError ? e.code : '';
      s.toast(
        code === 'NOT_FOUND'
          ? '✕ THAT REQUEST IS NO LONGER OPEN'
          : "✕ COULDN'T DO THAT — TRY AGAIN",
        true,
      );
      // Whatever refused us has probably changed the list.
      await get().loadJoinRequests();
    } finally {
      set({ jlBusy: '' });
    }
  },

  /**
   * Read the link and show the ASK screen.
   *
   * `peek` spends nothing, so this is safe to call again on a reload — which
   * matters, because the token lives in a URL fragment somebody may refresh.
   */
  openAsk: async (token) => {
    // Survives a reload from here until the ask is finished or abandoned.
    rememberAsk(token);
    set({
      askToken: token,
      askPeek: null,
      askEmail: '',
      askCodeSent: false,
      askMasked: '',
      askCode: '',
      askName: '',
      askPw: '',
      askPw2: '',
      askErr: '',
      askKnown: false,
      askBusy: true,
      screen: 'ask',
    });
    try {
      set({ askPeek: await get().repo.peekJoinLink(token) });
    } catch {
      // An unreadable link and an invalid one look the same to the reader,
      // which is also what the server intends.
      set({
        askPeek: {
          state: 'invalid',
          crewName: '',
          crewSlug: '',
          leaderName: '',
          alreadyMember: false,
        },
      });
    } finally {
      set({ askBusy: false });
    }
  },

  sendAskCode: async () => {
    const s = get();
    if (s.askBusy || !s.askToken) return;
    const addr = s.askEmail.trim();
    if (!EMAIL_RE.test(addr)) return set({ askErr: "THAT DOESN'T LOOK LIKE AN EMAIL" });

    set({ askBusy: true, askErr: '', askKnown: false });
    try {
      const res = await s.repo.startJoinCode(s.askToken, addr);
      set({ askCodeSent: true, askMasked: res.emailMasked, askCode: '' });
    } catch (e) {
      const code = e instanceof RepoError ? e.code : '';
      /* CAUGHT AT THE EMAIL STEP NOW, which is the whole point of moving the
         check: they find out before a code is spent, not after. `askKnown`
         drives a LOG IN door rather than an error line, because "log in" is
         the only thing that can help and retyping the address cannot. */
      set({
        askKnown: code === 'EMAIL_TAKEN',
        askErr:
          code === 'EMAIL_TAKEN'
            ? 'YOU ALREADY HAVE AN ACCOUNT ON THIS EMAIL.'
            : code === 'RATE_LIMITED'
            ? 'A CODE JUST WENT OUT. CHECK YOUR MAIL.'
            : code === 'NO_MAIL'
              ? "WE COULDN'T SEND THAT CODE RIGHT NOW. TRY AGAIN IN A WHILE."
              : code === 'REQUESTS_CLOSED'
                ? "THIS PARTY ISN'T TAKING REQUESTS RIGHT NOW."
                : code === 'LINK_CLOSED'
                  ? "THIS JOIN LINK ISN'T VALID."
                  : code === 'EMAIL_INVALID'
                    ? "THAT DOESN'T LOOK LIKE AN EMAIL"
                    : "COULDN'T SEND THAT CODE — TRY AGAIN",
      });
    } finally {
      set({ askBusy: false });
    }
  },

  /**
   * Ask to join — both arms.
   *
   * Signed in, this is one call and no form. Signed out, it spends the code and
   * creates the account first.
   */
  submitAsk: async () => {
    const s = get();
    if (s.askBusy || !s.askToken) return;
    const signedIn = Boolean(s.session);

    if (!signedIn) {
      if (!s.askCode.trim()) return set({ askErr: 'ENTER THE CODE FROM YOUR EMAIL' });
      if (!s.askName.trim()) return set({ askErr: 'CHOOSE A NAME' });
      if (s.askPw.length < 8) return set({ askErr: 'USE AT LEAST 8 CHARACTERS' });
      // Checked BEFORE the code is spent. A mismatch caught here costs a
      // retype; caught after would cost the code and the attempt.
      if (s.askPw !== s.askPw2) return set({ askErr: 'PASSWORDS DO NOT MATCH' });
    }

    set({ askBusy: true, askErr: '' });
    try {
      const res = signedIn
        ? await s.repo.requestJoin(s.askToken)
        : await s.repo.verifyJoinCode({
          token: s.askToken,
          email: s.askEmail.trim(),
          code: s.askCode.trim(),
          displayName: s.askName.trim(),
          password: s.askPw,
        });

      if (res.createdAccount) {
        /* They have an account now but no session — the server created it, it
           did not sign them in. Do that with what they just chose, which is the
           only moment the password is in hand.

           SIGN IN WITH THE ADDRESS, NOT THE NAME, for the reason `acceptJoin`
           records: an account made this way logs in at the address, and every
           other account in the product logs in at a name-derived one. */
        const identity = res.email ?? s.askName.trim();
        try {
          const session = await get().repo.signIn(identity, s.askPw);
          set({
            session: session.userKey,
            userId: session.userId,
            pfAuthEmail: session.authEmail,
          });
        } catch {
          // The account exists and the ask is recorded, so this must not look
          // like a failure. Send them to sign in with the address they now own.
          forgetAsk();
          set({ askToken: '', askPeek: null, askPw: '', askPw2: '', sel: identity });
          get().nav('login');
          get().toast('ASKED TO JOIN ' + res.crewName.toUpperCase() + ' — SIGN IN TO SEE');
          return;
        }
      }

      /* STRAIGHT TO WAITING, and NOT to the board — they are not in yet. This
         is the whole difference from `acceptJoin`, which lands on `tonight`
         because accepting an invitation IS joining. */
      forgetAsk();
      set({
        ...clearedCrewState(),
        activeCrew: null,
        askToken: '',
        askPeek: null,
        askCode: '',
        askPw: '',
        askPw2: '',
        askCodeSent: false,
      });
      await get().loadAskStatus();
      get().nav('waiting');
    } catch (e) {
      const code = e instanceof RepoError ? e.code : '';
      if (code === 'REQUEST_OPEN') {
        // Not a failure: the state they wanted is the state they have.
        forgetAsk();
        set({ askToken: '', askPeek: null, askErr: '' });
        await get().loadAskStatus();
        get().nav('waiting');
        return;
      }
      set({
        askErr:
          code === 'CODE_INVALID'
            ? "THAT CODE ISN'T RIGHT"
            : code === 'CODE_EXPIRED'
              ? 'THAT CODE HAS EXPIRED — ASK FOR A NEW ONE.'
              : code === 'CODE_LOCKED'
                ? 'TOO MANY TRIES. ASK FOR A NEW CODE.'
                : code === 'EMAIL_TAKEN'
                  ? 'YOU ALREADY HAVE AN ACCOUNT ON THIS EMAIL. LOG IN, THEN OPEN THIS LINK AGAIN.'
                  : code === 'ALREADY_MEMBER'
                    ? "YOU'RE ALREADY IN THIS PARTY."
                    : code === 'WEAK_PASSWORD'
                      ? 'USE AT LEAST 8 CHARACTERS'
                      : code === 'REQUESTS_CLOSED'
                        ? "THIS PARTY ISN'T TAKING REQUESTS RIGHT NOW."
                        : code === 'LINK_CLOSED'
                          ? "THIS JOIN LINK ISN'T VALID."
                          : "COULDN'T ASK TO JOIN — TRY AGAIN",
      });
    } finally {
      set({ askBusy: false });
    }
  },

  /**
   * Am I waiting? Returns whether there is a live request, so boot can decide
   * a screen without reading the store back.
   *
   * SWALLOWS ITS OWN FAILURE ON PURPOSE. This sits on the crewless boot path —
   * every account with no party, including one just removed from its last — and
   * a rejection here must fall through to the old behaviour rather than block.
   */
  loadAskStatus: async () => {
    try {
      const st = await get().repo.joinRequestStatus();
      set({ askWaiting: st.waiting ? st : null });
      return st.waiting;
    } catch {
      set({ askWaiting: null });
      return false;
    }
  },

  withdrawAsk: async () => {
    const s = get();
    if (s.askBusy) return;
    set({ askBusy: true });
    try {
      await s.repo.withdrawJoinRequest();
      forgetAsk();
      set({ askWaiting: null, askToken: '' });
      get().nav('startCrew');
      s.toast('REQUEST WITHDRAWN');
    } catch {
      s.toast("✕ COULDN'T WITHDRAW THAT — TRY AGAIN", true);
    } finally {
      set({ askBusy: false });
    }
  },
});
